import ky from "@/shared/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PaginatedResource, Resource } from "@/shared/types.ts";
import { PackagesPriceEntity } from "@/features/packages-price/type.ts";

type CreatePackagesPrice = {
  isActive: boolean;
  name: string;
  description: string;
  image: string;
  price: number;
};

type EditPackagesPrice = {
  id: number;
} & CreatePackagesPrice;

type DeletePackagesPrice = {
  packagesId: number;
};

export function useCreatePackagesPrice() {
  const queryClient = useQueryClient();

  return useMutation<Resource<PackagesPriceEntity>, Error, CreatePackagesPrice>(
    {
      mutationFn: (data: CreatePackagesPrice) =>
        ky.post("/api/v1/tariffs/package/price", { json: data }).json(),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["packages_price"] });
      },
    },
  );
}

export function usePackagesPriceById({
  packagesId,
  enabled = true,
}: {
  packagesId: number | string;
  enabled?: boolean;
}) {
  return useQuery<Resource<PackagesPriceEntity> | undefined>({
    queryKey: ["packages_price", `packages_price_${packagesId}`],
    queryFn: (ctx) =>
      ky
        .get<
          Resource<PackagesPriceEntity> | undefined
        >(`/api/v1/tariffs/package/price/${packagesId}`, { signal: ctx.signal })
        .json(),
    enabled,
  });
}

export function usePackagesPrices() {
  return useQuery<PaginatedResource<PackagesPriceEntity[]>>({
    queryKey: ["packages_price"],
    queryFn: (ctx) =>
      ky
        .get<
          Resource<PackagesPriceEntity>
        >(`/api/v1/tariffs/package/price`, { signal: ctx.signal })
        .json(),
    initialData: { data: [], total: 0 } as PaginatedResource<
      PackagesPriceEntity[]
    >,
  });
}

export function useEditPackagesPrice() {
  const queryClient = useQueryClient();

  return useMutation<Resource<PackagesPriceEntity>, Error, EditPackagesPrice>({
    mutationFn: (data: EditPackagesPrice) =>
      ky.put("/api/v1/tariffs/package/price", { json: data }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages_price"] });
    },
  });
}

export function useDeletePackagesPrice() {
  const queryClient = useQueryClient();

  return useMutation<Resource<PackagesPriceEntity>, Error, DeletePackagesPrice>(
    {
      mutationFn: (data: DeletePackagesPrice) =>
        ky
          .delete(`/api/v1/tariffs/package/price/${data.packagesId}`, {
            json: data,
          })
          .json(),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["packages_price"] });
      },
    },
  );
}
